import React, { useState } from 'react';
import { Brain, Loader2, CheckCircle, AlertCircle, Info, Sparkles } from 'lucide-react';
import { getChatRequestUrl } from '../config';
import { useAuth } from '../hooks/useAuth';

const ProductAnalysisButton = ({ product, onAnalysisComplete, userId, showInChat = false, compact = false }) => {
  const { getAuthSession } = useAuth();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    
    try {
      // Get fresh authentication token
      const { tokens } = await getAuthSession();
      const accessToken = tokens?.accessToken;
      
      if (!accessToken) {
        throw new Error('Authentication required. Please refresh the page and sign in again.');
      }
      
      // Call backend to analyze the product (use same URL as chat for correct env routing)
      const response = await fetch(getChatRequestUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          analyzeProduct: product,
          userId: userId
        })
      });

      if (response.status === 401) {
        throw new Error('Session expired. Please refresh the page and sign in again.');
      }

      if (!response.ok) {
        throw new Error('Analysis failed');
      }

      const result = await response.json();
      if (result.success && result.analysis) {
        setAnalysisResult(result.analysis);
        
        // Only send to chat if showInChat is true
        if (showInChat && window.addProductAnalysisToChat) {
          window.addProductAnalysisToChat({
            type: 'product_analysis',
            data: result.analysis,
            productName: product.name,
            productBrand: product.brand,
            productImage: product.image || product.image_base64
          });
        }
        
        // Save to product in backend
        onAnalysisComplete(product.id, result.analysis);
      } else {
        throw new Error(result.message || 'Analysis failed');
      }
      
    } catch (error) {
      console.error('Analysis error:', error);
      // Show error toast or handle gracefully
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getCompatibilityIcon = (compatibility) => {
    switch (compatibility) {
      case 'high':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'medium':
        return <Info className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Brain className="h-4 w-4 text-[#2A324B]" />;
    }
  };

  const getCompatibilityColor = (compatibility) => {
    switch (compatibility) {
      case 'high':
        return 'bg-green-50 border-green-200';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200';
      case 'low':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-[#F3D5B5]/30 border-[#F3D5B5]/50';
    }
  };

  // Compact variant (shelf detail action row): one clean button matching ActionBtn,
  // no subtitle/extra margin — so a not-yet-analysed product's row looks like the rest.
  if (compact) {
    return (
      <button type="button" onClick={handleAnalyze} disabled={isAnalyzing}
        className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2 px-2 text-[13px] font-medium whitespace-nowrap transition-colors disabled:opacity-60"
        style={{ background: '#fff', color: '#2A1212', border: '1px solid #D9D2C6' }}>
        {isAnalyzing
          ? <><Loader2 className="h-4 w-4 animate-spin" /> Analysing…</>
          : <><Sparkles className="h-4 w-4" /> Analyse</>}
      </button>
    );
  }

  // If product already has analysis, show the results
  if (product.ai_analysis || analysisResult) {
    const analysis = product.ai_analysis || analysisResult;
    console.log('Analysis data:', analysis);
    
    // Check if analysis has the expected structure
    if (analysis && typeof analysis === 'object' && analysis.compatibility && analysis.score !== undefined) {
      return (
        <div className={`mt-3 p-3 rounded-lg border ${getCompatibilityColor(analysis.compatibility)}`}>
          <div className="flex items-center gap-2 mb-2">
            {getCompatibilityIcon(analysis.compatibility)}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-[#2A324B]">
                AI Analysis: {analysis.score}% Match
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                analysis.compatibility === 'high' 
                  ? 'bg-green-100 text-green-700 border border-green-300'
                  : analysis.compatibility === 'medium'
                  ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                  : 'bg-red-100 text-red-700 border border-red-300'
              }`}>
                {analysis.compatibility.toUpperCase()}
              </span>
            </div>
          </div>
          <p className="text-xs text-[#2A324B]/80 leading-relaxed mb-2">
            {analysis.reasoning}
          </p>
          {analysis.concerns && analysis.concerns.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#2A324B] mb-1">Considerations:</p>
              <ul className="text-xs text-[#2A324B]/70 space-y-0.5">
                {analysis.concerns.map((concern, index) => (
                  <li key={index} className="flex items-start gap-1">
                    <span className="text-yellow-500 mt-0.5">•</span>
                    {concern}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-2 pt-2 border-t border-current/20">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="text-xs text-[#2A324B]/70 hover:text-[#2A324B] transition-colors flex items-center gap-1"
            >
              <Brain className="h-3 w-3" />
              {isAnalyzing ? 'Re-analyzing...' : 'Re-analyze Product'}
            </button>
          </div>
        </div>
      );
    } else {
      // Fallback for malformed analysis data
      return (
        <div className="mt-3 p-3 rounded-lg border bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-2 mb-2">
            <Brain className="h-4 w-4 text-yellow-600" />
            <span className="text-xs font-medium text-[#2A324B]">
              AI Analysis Available
            </span>
          </div>
          <p className="text-xs text-[#2A324B]/80 leading-relaxed mb-2">
            Analysis completed but format needs updating. Please re-analyze for better display.
          </p>
          <div className="mt-2 pt-2 border-t border-current/20">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="text-xs text-[#2A324B]/70 hover:text-[#2A324B] transition-colors flex items-center gap-1"
            >
              <Brain className="h-3 w-3" />
              {isAnalyzing ? 'Re-analyzing...' : 'Re-analyze Product'}
            </button>
          </div>
        </div>
      );
    }
  }

  // If no analysis yet, show the analyze button
  return (
    <div className="mt-3">
      <button
        onClick={handleAnalyze}
        disabled={isAnalyzing}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#F3D5B5]/30 hover:bg-[#F3D5B5]/50 text-[#2A324B] rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
      >
        {isAnalyzing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Analyzing...
          </>
        ) : (
          <>
            <Brain className="h-4 w-4" />
            Analyze with AI
          </>
        )}
      </button>
      <p className="text-xs text-[#2A324B]/60 mt-1 text-center">
        Get personalized insights based on your routine
      </p>
    </div>
  );
};

export default ProductAnalysisButton;
